// ============================================================================
// use-quotemaker-review.ts
// ----------------------------------------------------------------------------
// When QuoteMaker has written the quote on screen, the Assistant is brought
// forward with QuoteMaker's review of it (services/quotemaker-review.ts) on
// arrival at Stage 4 and at Stage 5, once at each per written version on this
// device (Peter, 2026-11-01: "shown at stage 4 and 5"). The review card stays at the top
// of the Assistant at both stages until the builder closes it.
//
// It raises the Assistant the way the request-source check does
// (use-rfq-source-doubt.ts): the stage cockpit's Assistant tab, or the
// floating panel when no cockpit mounts after a short wait (a phone).
// Nothing is raised while QuoteMaker is still writing, on a job written by
// hand, or for a role with no Assistant.
// ============================================================================
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    markQuoteMakerReview,
    quoteMakerReviewOf,
    quoteMakerReviewToRaise,
    readQuoteMakerReviewLedger,
    type QuoteMakerReview,
    type QuoteMakerReviewLedger,
} from '../services/quotemaker-review';
import { RFQ_SOURCE_DOCK_ATTEMPTS, RFQ_SOURCE_DOCK_RETRY_MS, RFQ_SOURCE_RAISE_DELAY_MS } from './use-rfq-source-doubt';

/** The stage the review is shown at: Stage 5 is the quote open on the QuoteMaker page, Stage 4 the items under it. */
export function quoteMakerReviewStage({
    currentPage,
    stage,
    quoteEditorOpen,
}: {
    currentPage: string;
    stage: number;
    quoteEditorOpen: boolean;
}): 4 | 5 | null {
    if (currentPage !== 'quotemaker') return null;
    if (quoteEditorOpen) return 5;
    return Number(stage) === 4 ? 4 : null;
}

export interface UseQuoteMakerReviewArgs {
    projectId: string | null | undefined;
    currentPage: string;
    stage: number;
    quoteEditorOpen: boolean;
    quoteData: Record<string, any> | null | undefined;
    scope: Record<string, any>[] | null | undefined;
    /** True while QuoteMaker writes this job: its quote is not final yet. */
    writing: boolean;
    /** True when the builder writes this job by hand. */
    byHand: boolean;
    /** False for a role with no Assistant (site assessor). */
    assistantAvailable: boolean;
    /** Show the stage cockpit's Assistant tab. False when no cockpit can host it yet. */
    raiseInCockpit: () => boolean;
    /** Open the floating Assistant panel. */
    openFloatingAssistant: () => void;
}

export function useQuoteMakerReview({
    projectId,
    currentPage,
    stage,
    quoteEditorOpen,
    quoteData,
    scope,
    writing,
    byHand,
    assistantAvailable,
    raiseInCockpit,
    openFloatingAssistant,
}: UseQuoteMakerReviewArgs): { review: QuoteMakerReview | null; done: () => void } {
    const atStage = quoteMakerReviewStage({ currentPage, stage, quoteEditorOpen });
    const review = useMemo(
        () => (projectId && !byHand ? quoteMakerReviewOf(quoteData, Array.isArray(scope) ? scope : []) : null),
        [projectId, byHand, quoteData, scope],
    );
    const runId = review?.runId || null;
    const [ledger, setLedger] = useState<QuoteMakerReviewLedger>(() => readQuoteMakerReviewLedger());
    // Another tab or an earlier visit may have recorded it: read again for each version.
    useEffect(() => { setLedger(readQuoteMakerReviewLedger()); }, [runId]);

    const showable = !!runId && !!atStage && assistantAvailable && !writing;
    const toRaise = showable && quoteMakerReviewToRaise(ledger, runId!, atStage!) ? runId : null;
    useEffect(() => {
        if (!toRaise || !atStage) return undefined;
        const raisedAt = atStage;
        let attempts = 0;
        let timer = setTimeout(function raise() {
            attempts += 1;
            const docked = raiseInCockpit();
            if (!docked && attempts < RFQ_SOURCE_DOCK_ATTEMPTS) {
                timer = setTimeout(raise, RFQ_SOURCE_DOCK_RETRY_MS);
                return;
            }
            if (!docked) openFloatingAssistant();
            setLedger(markQuoteMakerReview(toRaise, raisedAt));
        }, RFQ_SOURCE_RAISE_DELAY_MS);
        return () => clearTimeout(timer);
        // The two callbacks are per-render arrows in App; the version and the stage decide.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [toRaise, atStage]);

    const done = useCallback(() => {
        if (runId) setLedger(markQuoteMakerReview(runId, 'done'));
    }, [runId]);

    return { review: showable && !ledger[runId!]?.done ? review : null, done };
}
